import Enumerable from "linq";
import { Size, Rectangle, Point, IContentSource } from "../../../../../base/domain/drawing";
import { Document, Section } from "../../../../../documaker/domain/document";
import { TypeKeys } from "../../../../../documaker/domain/document/items";
import { FormatReference } from "../../../../../documaker/domain/format";
import { ObjectTranslator } from "../../../../../documaker/infrastructure/document/ver2";
import { IFormatBean } from "../../../../../documaker/infrastructure/format/ver2/beans";
import { EditableDocumentImageItemTranslator } from "./objects";
import {
  EditableFormatDocument,
  ReferenceFileContentSource,
  StoragePlaceKinds,
} from "../../../../domain/editableFormat";
import {
  EditableFormatLayerImageSource,
  EditableFormatThumbnailImageSource,
  EditableFormatArchiveReferenceFileSource,
  EditableFormatTemporaryReferenceFileSource,
  EditableFormatReferenceFileSource,
  EditableFormatTemporaryImageSource,
} from "../../../../domain/editableFormat/sources";
import { IFormatObjectBean, IEditableFormatDocumentSectionBean } from "../../../../infrastructure/editableFormat/ver2/beans";
import { EditableFormatObjectTranslator } from "./EditableFormatObjectTranslator";

export class EditableDocumentObjectTranslator extends ObjectTranslator {
  private _ImageItemTranslator =  new EditableDocumentImageItemTranslator(this._getToken);
  private _formatObjectTranslator = new EditableFormatObjectTranslator();

  toEditableFormatDocument(getToken: () => string, document: Document, format: IFormatObjectBean, formatArchiveId: number): EditableFormatDocument {
    return new EditableFormatDocument(
      document.documentId,
      document.edition,
      document.attributes,
      document.itemSets,
      document.sections,
      document.extraFormats,
      document.hisInformation,
      document.referenceFiles,
      document.patientProfile,
      document.designatedDoctorInformations,
      document.isNewHisInformationApplied,
      document.editToken,
      document.createToken,
      document.writeToken,
      document.selectedInstitution,
      document.unmanagedInformation,
      this.toReferenceFileContentSource(getToken, document, format, formatArchiveId),
      document.bookmarks,
    );
  }

  toReferenceFileContentSource(getToken: () => string, document: Document, format: IFormatObjectBean, formatArchiveId: number): ReferenceFileContentSource[] {
    return format
      ? Enumerable.from(format.referenceFiles)
        .select((r, i) => new ReferenceFileContentSource(
          i,
          this.setReferenceContentSource(getToken, document, format, r.fileKey, formatArchiveId)
        ))
        .toArray()
      : Enumerable.from(document.referenceFiles)
        .select((r) => new ReferenceFileContentSource(
          r.id,
          this.setReferenceContentSource(getToken, document, format, r.id, formatArchiveId)
        ))
        .toArray();
  }

  protected createReferenceFileContentSource(getToken: () => string, id: number, document: Document, format: IFormatObjectBean, fileId: number | string, formatArchiveId: number): ReferenceFileContentSource {
    return new ReferenceFileContentSource(
      id,
      this.setReferenceContentSource(getToken, document, format, fileId, formatArchiveId)
    );
  }

  protected setReferenceContentSource(getToken: () => string, document: Document, format: IFormatObjectBean, fileId: number | string, formatArchiveId: number): IContentSource {
    return format
      ? this.setEditingFormatReferenceFile(getToken, fileId, format)
      : new EditableFormatArchiveReferenceFileSource(getToken, document.attributes.format.id, Number(fileId), formatArchiveId);
  }

  protected setEditingFormatReferenceFile(getToken: () => string, fileId: number | string, format: IFormatObjectBean): IContentSource {
    const referenceFile = Enumerable.from(format.referenceFiles)
      .where(r => r.fileKey === fileId)
      .firstOrDefault();
    return referenceFile.storagePlace === StoragePlaceKinds.Cache
      ? new EditableFormatTemporaryReferenceFileSource(getToken, fileId.toString())
      : new EditableFormatReferenceFileSource(getToken, format.formatInfo.id, Number(fileId));
  }

  protected getTranslator(typeKey: string) {
    return (typeKey === TypeKeys.ImageItem || typeKey === TypeKeys.MovableImageItem)
      ? this._ImageItemTranslator
      : this.getTranslatorByType(typeKey);
  }

  protected toSection(getToken: () => string, documentId: number, formatId: number, section: IEditableFormatDocumentSectionBean): Section {
    const itemPrivateIds = Array<number>();
    for (const object of section.objects) {
      itemPrivateIds.push((object as any).privateId);
    }
    return new Section(
      new Size(section.size.width, section.size.height),
      new Rectangle(
        new Point(
          section.bodyRectangle.topLeft.x,
          section.bodyRectangle.topLeft.y),
        new Size(
          section.bodyRectangle.size.width,
          section.bodyRectangle.size.height)),
      this.getLayerImageSource(getToken, section),
      new Rectangle(
        new Point(
          section.layerImageOffset.x,
          section.layerImageOffset.y),
        new Size(
          section.layerImageSize.width,
          section.layerImageSize.height)),
      new EditableFormatThumbnailImageSource(getToken, section.formatId, section.formatSectionNumber),
      section.groupId,
      itemPrivateIds,
      section.landscape,
      section.formatSectionNumber,
      section.formatId,
      section.pageKey,
      !!section.isPageSplittable,
      !!section.printOnSaving,
      false,
      section.splitRedundantMargin);
  }

  protected toFormats(getToken: () => string, beans: IFormatBean[]): FormatReference[] {
    return this._formatObjectTranslator.toFormats(getToken, beans);
  }

  getLayerImageSource(getToken: () => string, section: IEditableFormatDocumentSectionBean): IContentSource {
    // 下地登録済みフォーマットの編集中に下地を削除して未登録状態の場合、
    // section.layerStoragePlace は StoragePlaceKinds.Format だが、この場合formatIdで下地を取得すると登録済みの下地を表示してしまう。
    // 下地未指定(下地を削除した場合も)だとsection.layerImageKey は null なので、
    // その場合はEditableFormatTemporaryImageSourceを指定して登録済み下地を表示しないようにしている
    return section.layerStoragePlace === StoragePlaceKinds.Cache || section.layerImageKey === null
      ? new EditableFormatTemporaryImageSource(this._getToken, 2, section.layerImageKey)
      : new EditableFormatLayerImageSource(getToken, section.formatId, section.layerImageKey);
  }
}
